import {AiProductType, ProductType} from 'common/src/modules/products/products.enums';
import {Routes} from 'common/src/modules/routing/routing.config';
import routingService from 'common/src/modules/routing/routing.service';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {z} from 'zod';
import {creativeTemplatesApi} from '../../../../creativeTemplates.api';
import {AiCreativeTemplateType, CreativeTemplateType} from '../../../../creativeTemplates.types';
import {CreativeTemplatesManagerAgentToolName} from '../creativeTemplatesManagetAgent.enums';

const inputSchema = z.object({
  name: z.string(),
  type: z.nativeEnum(AiCreativeTemplateType),
  // Need to support upload zip file in order to support custom code
  // source: z.nativeEnum(AiCreativeTemplateSource),
  isSupportLocationFeature: z.boolean().nullable().optional(),
  isSupportWeatherFeature: z.boolean().nullable().optional(),
  isSupportContentFeature: z.boolean().nullable().optional(),
  isSupportProductFeature: z.boolean().nullable().optional(),
  productType: z
    .nativeEnum(AiProductType)
    .nullable()
    .optional()
    .describe('If productFeatureSupport is true, this is required'),
});

function createCreativeTemplateTool() {
  async function execute({
    input: {
      name,
      type,
      productType,
      // source,
      isSupportLocationFeature = false,
      isSupportWeatherFeature = false,
      isSupportContentFeature = false,
      isSupportProductFeature = false,
    },
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    try {
      // const supportedSources = [CreativeTemplateSource.ClinchSDK, CreativeTemplateSource.AdBuilder];
      const templateData = {
        name,
        type: getMatchingEnumValue(AiCreativeTemplateType, CreativeTemplateType, type),
        productType: isSupportProductFeature
          ? getMatchingEnumValue(AiProductType, ProductType, productType)
          : ProductType.Promo,
        // source: getMatchingEnumValue(supportedSources, CreativeTemplateSource, source),
        locationSupport: isSupportLocationFeature,
        weatherSupport: isSupportWeatherFeature,
        contentSupport: isSupportContentFeature,
      };

      const newTemplateId = await creativeTemplatesApi.create(templateData);

      // Navigate to the template workflow page after successful creation
      routingService.goTo({
        url: Routes.TemplateWorkflow,
        pathParams: {templateId: newTemplateId},
      });

      return toolResult(
        JSON.stringify({
          message: `Creative template "${name}" created successfully with ID: ${newTemplateId}`,
          templateId: newTemplateId,
          templateData,
        }),
      );
    } catch (error) {
      return toolResult(
        JSON.stringify({
          error: `Failed to create creative template: ${error instanceof Error ? error.message : 'Unknown error'}`,
        }),
      );
    }
  }

  return tool({
    name: CreativeTemplatesManagerAgentToolName.CreateCreativeTemplate,
    description: 'Create a new creative template with specified parameters',
    displayName: 'Creating creative template',
    inputSchema,
    execute,
  });
}

export default createCreativeTemplateTool;
